/**
 * CloudEvents v1.0 Types
 *
 * These types define the structure for WebSocket events following the CloudEvents specification.
 * See: https://cloudevents.io/
 *
 * Key changes from legacy events:
 * 1. Event type names use `com.focal.*` format
 * 2. Payload is nested in `data` property
 * 3. Minimal data payloads (IDs, enums, timestamps only - no PII, no full objects)
 * 4. Includes metadata (id, time, source, subject) for debugging/correlation
 */

import type { ProcessingStepStatus, ProcessingStepType } from "./conversation";
import type {
  DeliverableBuildStage,
  DeliverableFailureReason,
  DeliverableStatus,
} from "./deliverable";

/**
 * Base CloudEvent interface following CloudEvents v1.0 specification
 */
export interface CloudEvent<T = unknown> {
  // Required CloudEvents attributes
  specversion: "1.0";
  id: string; // UUID - unique per event
  type: string; // e.g., "com.focal.task.created"
  source: string; // e.g., "/services/conversation-task"
  time: string; // ISO 8601 timestamp
  datacontenttype: "application/json";

  // Optional CloudEvents attributes
  subject?: string; // Resource identifier, e.g., "task/abc-123"

  // Payload - minimal data (IDs, enums, timestamps only)
  data: T;

  // Added by WebSocket layer
  userId: string; // Target user ID
}

// ============================================================================
// Event Type Constants
// ============================================================================

export const CloudEventType = {
  // Task events
  TASK_CREATED: "com.focal.task.created",
  TASK_UPDATED: "com.focal.task.updated",
  TASK_DELETED: "com.focal.task.deleted",
  TASK_BULK_UPDATED: "com.focal.task.bulk.updated",

  // Meeting events
  MEETING_CREATED: "com.focal.meeting.created",
  MEETING_UPDATED: "com.focal.meeting.updated",
  MEETING_DELETED: "com.focal.meeting.deleted",
  MEETING_STATUS_UPDATED: "com.focal.meeting.status.updated",

  // Conversation events
  CONVERSATION_CREATED: "com.focal.conversation.created",
  CONVERSATION_UPDATED: "com.focal.conversation.updated",
  CONVERSATION_STATUS_UPDATED: "com.focal.conversation.status.updated",
  CONVERSATION_PROCESSING_COMPLETED:
    "com.focal.conversation.processing.completed",
  COACHING_COMPLETED: "com.focal.conversation.coaching.completed",
  DATA_EXTRACTION_COMPLETED: "com.focal.conversation.data-extraction.completed",
  SENTIMENT_COMPLETED: "com.focal.conversation.sentiment.completed",
  PROCESSING_STEP_STATUS_CHANGED:
    "com.focal.conversation.processing-step.status-changed",

  // Deliverable events
  DELIVERABLE_STATUS_UPDATED: "com.focal.deliverable.status.updated",
  DELIVERABLE_EDIT_UPDATED: "com.focal.deliverable.edit.updated",

  // Billing events (Customer Admin)
  BILLING_STATUS_UPDATED: "com.focal.billing.status.updated",

  // Firm events (Customer Admin)
  FIRM_USERS_UPDATED: "com.focal.firm.users.updated",

  // Client events
  CLIENT_UPDATED: "com.focal.client.updated",
  CLIENT_CREATED: "com.focal.client.created",
  CLIENT_DELETED: "com.focal.client.deleted",

  // Participant events
  PARTICIPANT_CREATED: "com.focal.participant.created",
  PARTICIPANT_UPDATED: "com.focal.participant.updated",
  PARTICIPANT_DELETED: "com.focal.participant.deleted",

  // Permission events
  PERMISSION_CREATED: "com.focal.permission.created",
  PERMISSION_DELETED: "com.focal.permission.deleted",

  // Data table row events
  DATA_TABLE_ROW_UPDATED: "com.focal.data-table-row.updated",
  DATA_TABLE_ROW_DELETED: "com.focal.data-table-row.deleted",

  // Email follow-up events
  EMAIL_FOLLOWUP_REGENERATED: "com.focal.email-followup.regenerated",
  EMAIL_FOLLOWUP_UPDATED: "com.focal.email-followup.updated",

  // Meeting prep events
  MEETING_PREP_CREATED: "com.focal.meeting-prep.created",
  MEETING_PREP_UPDATED: "com.focal.meeting-prep.updated",
  MEETING_PREP_DELETED: "com.focal.meeting-prep.deleted",

  // Meeting prep email events
  MEETING_PREP_EMAIL_REGENERATED: "com.focal.meeting-prep-email.regenerated",
  MEETING_PREP_EMAIL_UPDATED: "com.focal.meeting-prep-email.updated",

  // Calendar events:
  CALENDAR_UPDATED: "com.focal.calendar.updated",
  CALENDAR_CONNECTED: "com.focal.calendar.connected",
  CALENDAR_DISCONNECTED: "com.focal.calendar.disconnected",

  // Integration events:
  INTEGRATION_STATUS_CHANGED: "com.focal.integration.status.changed",
} as const;

export type CloudEventTypeValue =
  (typeof CloudEventType)[keyof typeof CloudEventType];

/**
 * The Socket.IO event name the monolith emits deliverable lifecycle updates on.
 *
 * The shared provider routes on the CloudEvent envelope's `type`
 * (`useWebSocket`'s `onAny` handler), not on the Socket.IO event name, so this
 * is documentation plus a fallback: a payload whose `type` still carries the
 * raw Socket.IO name instead of the `com.focal.*` CloudEvent type is treated as
 * the same event.
 */
export const DELIVERABLE_STATUS_UPDATED_SOCKET_EVENT =
  "deliverable:status:updated";

/** True for either spelling of the deliverable lifecycle event type. */
export function isDeliverableStatusUpdatedType(type: string): boolean {
  return (
    type === CloudEventType.DELIVERABLE_STATUS_UPDATED ||
    type === DELIVERABLE_STATUS_UPDATED_SOCKET_EVENT
  );
}

// ============================================================================
// Event Data Interfaces
// ============================================================================

// Task Events
export interface TaskCreatedData {
  taskId: string;
  conversationId: string;
}

export interface TaskUpdatedData {
  taskId: string;
  conversationId: string;
  scope?: string[]; // Which fields changed
}

export interface TaskDeletedData {
  taskId: string;
  conversationId: string;
}

export interface TaskBulkUpdatedData {
  taskIds: string[];
  conversationId: string;
}

// Meeting Events
export interface MeetingCreatedData {
  meetingId: string;
}

export interface MeetingUpdatedData {
  meetingId: string;
  scope?: string[]; // Which fields changed
}

export interface MeetingDeletedData {
  meetingId: string;
}

export interface MeetingStatusUpdatedData {
  meetingId: string;
  status: string;
}

// Conversation Events
export interface ConversationCreatedData {
  conversationId: string;
}

export interface ConversationUpdatedData {
  conversationId: string;
  scope?: string[];
}

export interface ConversationStatusUpdatedData {
  conversationId: string;
  status: string; // e.g., "PROCESSING", "DONE", "ERROR_SUMMARY"
}

export interface ConversationProcessingCompletedData {
  conversationId: string;
  status: string;
}

export interface CoachingCompletedData {
  conversationId: string;
  status: string;
}

export interface DataExtractionCompletedData {
  conversationId: string;
  status: string;
}

export interface SentimentCompletedData {
  conversationId: string;
}

export interface ProcessingStepStatusChangedData {
  conversationId: string;
  step: ProcessingStepType;
  status: ProcessingStepStatus;
  errorReason?: string;
  updatedAt: string;
}

// Deliverable Events
export interface DeliverableStatusUpdatedData {
  deliverableId: string;
  status: DeliverableStatus;
  stage: DeliverableBuildStage | null;
  failureReason: DeliverableFailureReason | null;
  /**
   * The build run this update belongs to. Without it a delayed terminal event
   * from an earlier attempt — a retry, or a resumed worker — is
   * indistinguishable from the live one, because a deliverable id alone names
   * every attempt ever made on that row.
   *
   * Optional because the worker does not emit it yet: consumers key
   * notifications by deliverable *and* attempt where it is present, and fall
   * back to event ordering where it is not.
   */
  attemptId?: string | null;
}

// Billing Events
export interface BillingStatusUpdatedData {
  firmId: string;
  status: string; // e.g., "ACTIVE", "TRIAL", "PAST_DUE", "CANCELED"
  subscriptionTier?: string; // e.g., "professional", "trial"
}

// Firm Events
export interface FirmUsersUpdatedData {
  firmId: string;
  action: "added" | "removed" | "updated";
  userCount: number;
}

// Client Events
export interface ClientUpdatedData {
  clientId: string;
}

export interface ClientCreatedData {
  clientId: string;
}

export interface ClientDeletedData {
  clientId: string;
}

// Participant Events
export interface ParticipantCreatedData {
  participantId: string;
  conversationId: string;
}

export interface ParticipantUpdatedData {
  participantId: string;
  conversationId: string;
  scope?: string[];
}

export interface ParticipantDeletedData {
  participantId: string;
  conversationId: string;
}

// Permission Events
export interface PermissionCreatedData {
  permissionId: string;
  entityType: "CONVERSATION" | "CLIENT";
  entityId: string;
}

export interface PermissionDeletedData {
  permissionId: string;
  entityType: "CONVERSATION" | "CLIENT";
  entityId: string;
}

// Data Table Row Events
export interface DataTableRowUpdatedData {
  rowId: string;
  conversationId: string;
  scope?: string[];
}

export interface DataTableRowDeletedData {
  rowId: string;
  conversationId: string;
}

// Email Follow-up Events
export interface EmailFollowupRegeneratedData {
  conversationId: string;
}

export interface EmailFollowupUpdatedData {
  conversationId: string;
}

// Meeting Prep Events
export interface MeetingPrepCreatedData {
  meetingPrepId: string;
}

export interface MeetingPrepUpdatedData {
  meetingPrepId: string;
  scope?: string[];
}

export interface MeetingPrepDeletedData {
  meetingPrepId: string;
}

// Meeting Prep Email Events
export interface MeetingPrepEmailRegeneratedData {
  meetingPrepId: string;
}

export interface MeetingPrepEmailUpdatedData {
  meetingPrepId: string;
}

// ============================================================================
// Typed CloudEvent Helpers
// ============================================================================

export type TaskCreatedEvent = CloudEvent<TaskCreatedData>;
export type TaskUpdatedEvent = CloudEvent<TaskUpdatedData>;
export type TaskDeletedEvent = CloudEvent<TaskDeletedData>;
export type TaskBulkUpdatedEvent = CloudEvent<TaskBulkUpdatedData>;

export type MeetingCreatedEvent = CloudEvent<MeetingCreatedData>;
export type MeetingUpdatedEvent = CloudEvent<MeetingUpdatedData>;
export type MeetingDeletedEvent = CloudEvent<MeetingDeletedData>;
export type MeetingStatusUpdatedEvent = CloudEvent<MeetingStatusUpdatedData>;

export type ConversationCreatedEvent = CloudEvent<ConversationCreatedData>;
export type ConversationUpdatedEvent = CloudEvent<ConversationUpdatedData>;
export type ConversationStatusUpdatedEvent =
  CloudEvent<ConversationStatusUpdatedData>;
export type ConversationProcessingCompletedEvent =
  CloudEvent<ConversationProcessingCompletedData>;
export type CoachingCompletedEvent = CloudEvent<CoachingCompletedData>;
export type DataExtractionCompletedEvent =
  CloudEvent<DataExtractionCompletedData>;
export type SentimentCompletedEvent = CloudEvent<SentimentCompletedData>;
export type ProcessingStepStatusChangedEvent =
  CloudEvent<ProcessingStepStatusChangedData>;

export type DeliverableStatusUpdatedEvent =
  CloudEvent<DeliverableStatusUpdatedData>;

export type BillingStatusUpdatedEvent = CloudEvent<BillingStatusUpdatedData>;
export type FirmUsersUpdatedEvent = CloudEvent<FirmUsersUpdatedData>;

export type ClientUpdatedEvent = CloudEvent<ClientUpdatedData>;
export type ClientCreatedEvent = CloudEvent<ClientCreatedData>;
export type ClientDeletedEvent = CloudEvent<ClientDeletedData>;

export type ParticipantCreatedEvent = CloudEvent<ParticipantCreatedData>;
export type ParticipantUpdatedEvent = CloudEvent<ParticipantUpdatedData>;
export type ParticipantDeletedEvent = CloudEvent<ParticipantDeletedData>;

export type PermissionCreatedEvent = CloudEvent<PermissionCreatedData>;
export type PermissionDeletedEvent = CloudEvent<PermissionDeletedData>;

export type DataTableRowUpdatedEvent = CloudEvent<DataTableRowUpdatedData>;
export type DataTableRowDeletedEvent = CloudEvent<DataTableRowDeletedData>;

export type EmailFollowupRegeneratedEvent =
  CloudEvent<EmailFollowupRegeneratedData>;
export type EmailFollowupUpdatedEvent = CloudEvent<EmailFollowupUpdatedData>;

export type MeetingPrepCreatedEvent = CloudEvent<MeetingPrepCreatedData>;
export type MeetingPrepUpdatedEvent = CloudEvent<MeetingPrepUpdatedData>;
export type MeetingPrepDeletedEvent = CloudEvent<MeetingPrepDeletedData>;
export type MeetingPrepEmailRegeneratedEvent =
  CloudEvent<MeetingPrepEmailRegeneratedData>;
export type MeetingPrepEmailUpdatedEvent =
  CloudEvent<MeetingPrepEmailUpdatedData>;

// Helper type for event handlers
export type CloudEventHandler<T> = (
  event: CloudEvent<T>,
) => void | Promise<void>;

// ============================================================================
// Channel to Event Type Mapping
// ============================================================================

/**
 * Maps subscription channels to their associated CloudEvent types
 */
export const ChannelEventTypes = {
  conversation: [
    CloudEventType.CONVERSATION_CREATED,
    CloudEventType.CONVERSATION_UPDATED,
    CloudEventType.CONVERSATION_STATUS_UPDATED,
    CloudEventType.CONVERSATION_PROCESSING_COMPLETED,
    CloudEventType.COACHING_COMPLETED,
    CloudEventType.DATA_EXTRACTION_COMPLETED,
    CloudEventType.SENTIMENT_COMPLETED,
    CloudEventType.PROCESSING_STEP_STATUS_CHANGED,
  ],
  deliverable: [
    CloudEventType.DELIVERABLE_STATUS_UPDATED,
    CloudEventType.DELIVERABLE_EDIT_UPDATED,
  ],
  task: [
    CloudEventType.TASK_CREATED,
    CloudEventType.TASK_UPDATED,
    CloudEventType.TASK_DELETED,
    CloudEventType.TASK_BULK_UPDATED,
  ],
  meeting: [
    CloudEventType.MEETING_CREATED,
    CloudEventType.MEETING_UPDATED,
    CloudEventType.MEETING_DELETED,
    CloudEventType.MEETING_STATUS_UPDATED,
  ],
  client: [
    CloudEventType.CLIENT_UPDATED,
    CloudEventType.CLIENT_CREATED,
    CloudEventType.CLIENT_DELETED,
  ],
  participant: [
    CloudEventType.PARTICIPANT_CREATED,
    CloudEventType.PARTICIPANT_UPDATED,
    CloudEventType.PARTICIPANT_DELETED,
  ],
  permission: [
    CloudEventType.PERMISSION_CREATED,
    CloudEventType.PERMISSION_DELETED,
  ],
  "data-table-row": [
    CloudEventType.DATA_TABLE_ROW_UPDATED,
    CloudEventType.DATA_TABLE_ROW_DELETED,
  ],
  "email-followup": [
    CloudEventType.EMAIL_FOLLOWUP_REGENERATED,
    CloudEventType.EMAIL_FOLLOWUP_UPDATED,
  ],
  "meeting-prep": [
    CloudEventType.MEETING_PREP_CREATED,
    CloudEventType.MEETING_PREP_UPDATED,
    CloudEventType.MEETING_PREP_DELETED,
    CloudEventType.MEETING_PREP_EMAIL_REGENERATED,
    CloudEventType.MEETING_PREP_EMAIL_UPDATED,
  ],
  billing: [CloudEventType.BILLING_STATUS_UPDATED],
  firm: [CloudEventType.FIRM_USERS_UPDATED],
  calendar: [
    CloudEventType.CALENDAR_UPDATED,
    CloudEventType.CALENDAR_CONNECTED,
    CloudEventType.CALENDAR_DISCONNECTED,
  ],
  integration: [CloudEventType.INTEGRATION_STATUS_CHANGED],
} as const;

export type ChannelName = keyof typeof ChannelEventTypes;

/**
 * Extract entity ID from CloudEvent based on event type
 */
export function getEntityIdFromCloudEvent(event: CloudEvent): string | null {
  const { type, data } = event;

  if (!data || typeof data !== "object") {
    return null;
  }

  const payload = data as Record<string, unknown>;

  // Deliverable events
  if (
    isDeliverableStatusUpdatedType(type) ||
    type.startsWith("com.focal.deliverable.")
  ) {
    return (payload.deliverableId as string) || null;
  }

  // Task events
  if (type.startsWith("com.focal.task.")) {
    return (payload.conversationId as string) || null;
  }

  // Participant events
  if (type.startsWith("com.focal.participant.")) {
    return (payload.conversationId as string) || null;
  }

  // Meeting prep events (prep doc + prep email) — keyed by meetingPrepId.
  // Checked before "com.focal.meeting." since both share the "meeting" stem.
  if (type.startsWith("com.focal.meeting-prep")) {
    return (payload.meetingPrepId as string) || null;
  }

  // Meeting events
  if (type.startsWith("com.focal.meeting.")) {
    return (payload.meetingId as string) || null;
  }

  // Conversation events
  if (type.startsWith("com.focal.conversation.")) {
    return (payload.conversationId as string) || null;
  }

  // Client events
  if (type.startsWith("com.focal.client.")) {
    return (payload.clientId as string) || null;
  }

  // Permission events
  if (type.startsWith("com.focal.permission.")) {
    return (payload.entityId as string) || null;
  }

  // Data table row events
  if (type.startsWith("com.focal.data-table-row.")) {
    return (payload.conversationId as string) || null;
  }

  // Email follow-up events
  if (type.startsWith("com.focal.email-followup.")) {
    return (payload.conversationId as string) || null;
  }

  // Calendar events
  if (type.startsWith("com.focal.calendar.")) {
    return (payload.calendarId as string) || null;
  }

  // Billing/Firm/Integration events (global, no entity ID)
  if (
    type.startsWith("com.focal.billing.") ||
    type.startsWith("com.focal.firm.") ||
    type.startsWith("com.focal.integration.")
  ) {
    return null;
  }

  return null;
}

/**
 * Get channel name from CloudEvent type
 */
export function getChannelFromCloudEventType(type: string): ChannelName | null {
  if (
    isDeliverableStatusUpdatedType(type) ||
    type.startsWith("com.focal.deliverable.")
  ) {
    return "deliverable";
  }
  if (type.startsWith("com.focal.task.")) return "task";
  if (type.startsWith("com.focal.participant.")) return "participant";
  if (type.startsWith("com.focal.meeting-prep")) return "meeting-prep";
  if (type.startsWith("com.focal.meeting.")) return "meeting";
  if (type.startsWith("com.focal.conversation.")) return "conversation";
  if (type.startsWith("com.focal.client.")) return "client";
  if (type.startsWith("com.focal.permission.")) return "permission";
  if (type.startsWith("com.focal.data-table-row.")) return "data-table-row";
  if (type.startsWith("com.focal.email-followup.")) return "email-followup";
  if (type.startsWith("com.focal.billing.")) return "billing";
  if (type.startsWith("com.focal.firm.")) return "firm";
  if (type.startsWith("com.focal.calendar.")) return "calendar";
  if (type.startsWith("com.focal.integration.")) return "integration";

  return null;
}
